import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractApiError } from '@/lib/api-error';
import {
  slugFromFileName,
  useDeleteHotkeyIcon,
  useHotkeyIcons,
  useUploadHotkeyIcon,
  type HotkeyIcon,
} from '@/hooks/use-hotkey-icons';
import { useT } from '@/i18n/use-t';

/**
 * Upload and manage custom hotkey icons (VL-260).
 *
 * The file is read in the browser and posted as text — an SVG is markup, and
 * sending it as a string keeps the request on the same validated JSON path as
 * every other write here.
 *
 * The preview renders each icon at 17 px inside a 30×30 container, which is
 * the Quick Actions geometry from the terminal prototype. That is the point of
 * showing it at all: an author needs to see the glyph at the size a cashier
 * will, not scaled up to fill a card, because a glyph that reads well at 96 px
 * and turns to mud at 17 px is the failure this preview is here to catch.
 */
export function IconUpload({ onPicked }: { onPicked?: (iconName: string) => void }) {
  const t = useT();
  const icons = useHotkeyIcons();
  const upload = useUploadHotkeyIcon();
  const remove = useDeleteHotkeyIcon();
  const fileRef = useRef<HTMLInputElement>(null);

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | undefined): Promise<void> {
    setError(null);
    if (file === undefined) return;
    const text = await file.text();
    setSvg(text);
    // Only prefill: an operator replacing an icon types the existing slug and
    // must not have it overwritten by whatever the new export was called.
    if (slug === '') setSlug(slugFromFileName(file.name));
    if (name === '') setName(file.name.replace(/\.svg$/i, ''));
  }

  async function handleUpload(): Promise<void> {
    setError(null);
    if (svg === null) {
      setError(t('hotkeys.icons.chooseFirst', 'Choose an .svg file first'));
      return;
    }
    try {
      const saved = await upload.mutateAsync({ slug, name, svg });
      setSvg(null);
      setSlug('');
      setName('');
      if (fileRef.current !== null) fileRef.current.value = '';
      onPicked?.(`custom:${saved.slug}`);
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  async function handleDelete(icon: HotkeyIcon): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync(icon.slug);
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <div className="grid gap-3 rounded-md border p-3">
      <div className="grid gap-1.5">
        <Label htmlFor="hk-icon-file">{t('hotkeys.icons.file', 'Custom icon (.svg)')}</Label>
        <input
          id="hk-icon-file"
          ref={fileRef}
          type="file"
          accept=".svg,image/svg+xml"
          className="text-xs"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </div>

      {svg !== null && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="hk-icon-slug">{t('catalog.common.slug', 'Slug')}</Label>
            <Input
              id="hk-icon-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="drinks"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="hk-icon-name">{t('catalog.common.name', 'Name')}</Label>
            <Input
              id="hk-icon-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Drinks"
            />
          </div>
        </div>
      )}

      {svg !== null && (
        <Button
          type="button"
          size="sm"
          onClick={() => void handleUpload()}
          disabled={upload.isPending}
        >
          {upload.isPending
            ? t('hotkeys.icons.uploading', 'Uploading…')
            : t('hotkeys.icons.upload', 'Upload icon')}
        </Button>
      )}

      {error !== null && (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      )}

      {(icons.data ?? []).length > 0 && (
        <div className="grid gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('hotkeys.icons.uploaded', 'Uploaded icons — shown at the 17 px Quick Actions size')}
          </p>
          <ul className="grid gap-1">
            {(icons.data ?? []).map((icon) => (
              <li key={icon.slug} className="flex items-center gap-2 text-xs">
                <span
                  // 30×30 container at 9 px radius, 17 px glyph — the prototype
                  // geometry recorded on VL-260.
                  className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] bg-muted"
                  aria-hidden
                >
                  <span
                    className="block h-[17px] w-[17px] [&>svg]:h-full [&>svg]:w-full"
                    // The markup is sanitised server-side against an allow-list
                    // before it is ever stored, and it is the same markup the
                    // tills receive. Rendering the unsanitised upload here
                    // instead would preview something no cashier will see.
                    dangerouslySetInnerHTML={{ __html: icon.svg }}
                  />
                </span>
                <button
                  type="button"
                  className="flex-1 text-left hover:underline"
                  onClick={() => onPicked?.(`custom:${icon.slug}`)}
                >
                  <span className="font-medium">{icon.name}</span>
                  <span className="ml-1 text-muted-foreground">custom:{icon.slug}</span>
                </button>
                <span className="text-muted-foreground">
                  {icon.usageCount === 0
                    ? t('hotkeys.icons.unused', 'unused')
                    : (icon.usageCount === 1
                        ? t('hotkeys.icons.usageOne', '{count} hotkey')
                        : t('hotkeys.icons.usageMany', '{count} hotkeys')
                      ).replace('{count}', String(icon.usageCount))}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleDelete(icon)}
                  disabled={remove.isPending}
                >
                  {t('catalog.common.delete', 'Delete')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
